import type {
  BeforeSendFn,
  CaptureResult,
  PostHog,
  PostHogConfig,
  Properties,
} from "posthog-js";
import {
  hasPerformanceCookieConsent,
  hasPostHogPersistenceConsent,
  hasPersonalizationCookieConsent,
  readActiveOneTrustGroups,
  subscribeToOneTrustCookieConsentChanges,
} from "./oneTrustCookieConsent";

const PERSONALIZATION_COOKIE_CONSENT_EVENT_NAMES = new Set([
  // identify() and people.set().
  "$identify",
  "$set",
]);

export const POSTHOG_EARLY_ACCESS_ENROLLMENT_UPDATE_EVENT =
  "$feature_enrollment_update";

// These events support account-specific feature access rather than analytics.
// They are allowed without optional cookie consent after their properties have
// been reduced to the minimum needed to identify the account or persist an
// explicit early-access choice.
const REQUIRED_FEATURE_ACCESS_EVENT_NAMES = new Set([
  "$identify",
  POSTHOG_EARLY_ACCESS_ENROLLMENT_UPDATE_EVENT,
]);

const PERSONALIZATION_ONLY_EVENT_PROPERTY_NAMES = new Set([
  "token",
  "distinct_id",
]);

const EARLY_ACCESS_ENROLLMENT_EVENT_PROPERTY_NAMES = new Set([
  ...PERSONALIZATION_ONLY_EVENT_PROPERTY_NAMES,
  "$feature_flag",
  "$feature_enrollment",
]);

const PERSONALIZATION_ONLY_PERSON_PROPERTY_NAMES = new Set([
  "activeOrgId",
  "email",
  "name",
]);

const REQUIRED_FUNCTIONAL_PERSON_PROPERTY_NAMES = new Set(["activeOrgId"]);

const EARLY_ACCESS_ENROLLMENT_PROPERTY_PREFIX = "$feature_enrollment/";

const PERSONALIZATION_ONLY_PRIVACY_EVENT_PROPERTIES: Properties = {
  $geoip_disable: true,
  $ip: null,
  $update_person_last_seen_at: false,
};

let currentOneTrustGroups = readActiveOneTrustGroups();
let currentPerformanceCookieConsent = hasPerformanceCookieConsent(
  currentOneTrustGroups,
);
let currentPersonalizationCookieConsent = hasPersonalizationCookieConsent(
  currentOneTrustGroups,
);
let postHogCookieConsentBridgeInitialized = false;

function isProperties(value: unknown): value is Properties {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

type PostHogPropertyNameFilter =
  | ReadonlySet<string>
  | ((propertyName: string) => boolean);

// Keep only PostHog properties whose names match an exact allowlist or
// predicate.
function pickPostHogProperties(
  properties: unknown,
  propertyNames: PostHogPropertyNameFilter,
): Properties | undefined {
  if (!isProperties(properties)) return undefined;

  const matchesPropertyName =
    typeof propertyNames === "function"
      ? propertyNames
      : (propertyName: string) => propertyNames.has(propertyName);

  const scrubbedProperties = Object.fromEntries(
    Object.entries(properties).filter(([propertyName]) =>
      matchesPropertyName(propertyName),
    ),
  ) as Properties;

  return Object.keys(scrubbedProperties).length
    ? scrubbedProperties
    : undefined;
}

// Add a PostHog person-property field only when it has an allowed value.
function withOptionalPersonProperties(
  propertyName: "$set" | "$set_once",
  properties: Properties | undefined,
): Properties {
  return properties ? { [propertyName]: properties } : {};
}

function isRequiredFunctionalPersonPropertyUpdate(
  event: CaptureResult,
): boolean {
  if (event.event !== "$set") return false;

  return [event.$set, event.properties.$set].some(
    (properties) =>
      isProperties(properties) &&
      Object.prototype.hasOwnProperty.call(properties, "activeOrgId"),
  );
}

function isEarlyAccessEnrollmentEvent(event: CaptureResult): boolean {
  return event.event === POSTHOG_EARLY_ACCESS_ENROLLMENT_UPDATE_EVENT;
}

function pickPersonPropertiesForFeatureAccessEvent(
  event: CaptureResult,
): (properties: unknown) => Properties | undefined {
  if (isEarlyAccessEnrollmentEvent(event)) {
    return (properties) =>
      pickPostHogProperties(
        properties,
        (propertyName) =>
          propertyName.startsWith(EARLY_ACCESS_ENROLLMENT_PROPERTY_PREFIX),
      );
  }

  if (currentPersonalizationCookieConsent) {
    return (properties) =>
      pickPostHogProperties(
        properties,
        PERSONALIZATION_ONLY_PERSON_PROPERTY_NAMES,
      );
  }

  if (isRequiredFunctionalPersonPropertyUpdate(event)) {
    return (properties) =>
      pickPostHogProperties(
        properties,
        REQUIRED_FUNCTIONAL_PERSON_PROPERTY_NAMES,
      );
  }

  return () => undefined;
}

// Remove analytics context from identity, enrollment, and required functional
// profile events. C0003 permits personalization fields; early-access and
// organization state remain available as essential functionality.
function scrubPostHogFeatureAccessEvent(event: CaptureResult): CaptureResult {
  const isEarlyAccessEnrollment = isEarlyAccessEnrollmentEvent(event);
  const pickPersonProperties =
    pickPersonPropertiesForFeatureAccessEvent(event);
  const eventSetProperties = pickPersonProperties(event.$set);
  const eventSetOnceProperties = pickPersonProperties(event.$set_once);
  const nestedSetProperties = pickPersonProperties(event.properties.$set);
  const nestedSetOnceProperties = pickPersonProperties(
    event.properties.$set_once,
  );
  const baseEventProperties =
    (isEarlyAccessEnrollment
      ? pickPostHogProperties(
          event.properties,
          EARLY_ACCESS_ENROLLMENT_EVENT_PROPERTY_NAMES,
        )
      : pickPostHogProperties(
          event.properties,
          PERSONALIZATION_ONLY_EVENT_PROPERTY_NAMES,
        )) || {};
  const scrubbedProperties: Properties = {
    ...baseEventProperties,
    ...PERSONALIZATION_ONLY_PRIVACY_EVENT_PROPERTIES,
    ...withOptionalPersonProperties("$set", nestedSetProperties),
    ...withOptionalPersonProperties("$set_once", nestedSetOnceProperties),
  };

  return {
    ...event,
    properties: scrubbedProperties,
    ...withOptionalPersonProperties("$set", eventSetProperties),
    ...withOptionalPersonProperties("$set_once", eventSetOnceProperties),
  };
}

export const cookieConsentAwareBeforeSend: BeforeSendFn = (
  event: CaptureResult | null,
): CaptureResult | null => {
  if (!event) return event;

  const requiresFeatureAccess = REQUIRED_FEATURE_ACCESS_EVENT_NAMES.has(
    event.event,
  );
  const requiresFunctionalPersonProperty =
    isRequiredFunctionalPersonPropertyUpdate(event);
  const requiresPersonalization =
    PERSONALIZATION_COOKIE_CONSENT_EVENT_NAMES.has(event.event);

  // Both consent levels are present - return the event as is.
  if (currentPerformanceCookieConsent && currentPersonalizationCookieConsent) {
    return event;
  }

  // Performance consent permits quantitative measurement. Identity and person
  // updates are personalization data, so they must not use this fast path.
  if (
    currentPerformanceCookieConsent &&
    !requiresFeatureAccess &&
    !requiresPersonalization
  ) {
    return event;
  }

  if (
    (currentPersonalizationCookieConsent && requiresPersonalization) ||
    requiresFeatureAccess ||
    requiresFunctionalPersonProperty
  ) {
    return scrubPostHogFeatureAccessEvent(event);
  }

  return null;
};

const basePostHogCookieConsentOptions: Partial<PostHogConfig> = {
  person_profiles: "identified_only",
  before_send: cookieConsentAwareBeforeSend,
};

function getPostHogCookieConsentOptions(
  hasPerformance: boolean,
  hasPersistenceCookieConsent: boolean,
): Partial<PostHogConfig> {
  return {
    ...basePostHogCookieConsentOptions,
    persistence: hasPersistenceCookieConsent ? "localStorage+cookie" : "memory",
    ip: hasPerformance,
    disable_session_recording: !hasPerformance,
  };
}

export function getInitialPostHogCookieConsentOptions(): Partial<PostHogConfig> {
  return getPostHogCookieConsentOptions(
    currentPerformanceCookieConsent,
    hasPostHogPersistenceConsent(currentOneTrustGroups),
  );
}

function applyOneTrustCookieConsentToPostHog(
  posthog: PostHog,
  groups: string[] = readActiveOneTrustGroups(),
  options: { isInitialSync?: boolean } = {},
): void {
  const hasPerformance = hasPerformanceCookieConsent(groups);
  const hasPersonalization = hasPersonalizationCookieConsent(groups);
  // Compute transitions before mutating module-level consent state.
  const personalizationCookieConsentChanged =
    currentPersonalizationCookieConsent !== hasPersonalization;
  const hadPersistenceCookieConsent =
    hasPostHogPersistenceConsent(currentOneTrustGroups);
  const hasPersistenceCookieConsent =
    hasPostHogPersistenceConsent(groups);

  if (
    !options.isInitialSync &&
    hadPersistenceCookieConsent &&
    !hasPersistenceCookieConsent
  ) {
    posthog.reset(true);
  }

  if (hasPerformance) {
    // Enable PostHog capture without emitting a separate $opt_in event.
    posthog.opt_in_capturing({ captureEventName: false });
  }

  currentPerformanceCookieConsent = hasPerformance;
  currentPersonalizationCookieConsent = hasPersonalization;
  currentOneTrustGroups = groups;
  posthog.set_config(
    getPostHogCookieConsentOptions(hasPerformance, hasPersistenceCookieConsent),
  );

  if (!options.isInitialSync && personalizationCookieConsentChanged) {
    posthog.reloadFeatureFlags();
  }
}

export function initializePostHogCookieConsentBridge(posthog: PostHog): void {
  if (postHogCookieConsentBridgeInitialized || typeof window === "undefined") {
    return;
  }
  postHogCookieConsentBridgeInitialized = true;

  applyOneTrustCookieConsentToPostHog(posthog, readActiveOneTrustGroups(), {
    isInitialSync: true,
  });

  subscribeToOneTrustCookieConsentChanges((groups) => {
    applyOneTrustCookieConsentToPostHog(posthog, groups);
  });
}
